import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { UntitledLogo } from '@/components/foundations/logo/untitledui-logo';
import { validatePassword } from '@kordbooks/shared-auth';
import { Eye, EyeOff } from '@untitledui/icons';


export default function ForgotPasswordReset() {
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Ensure a valid recovery session exists (either from magic link or verifyOtp)
  useEffect(() => {
    const checkSession = async () => {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.getUser();

      if (error || !data?.user) {
        navigate('forgot-password', { replace: true });
      }
    };

    void checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!password) {
      setError('New password is required');
      return;
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      const supabase = getSupabaseClient();

      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        throw updateError;
      }

      setMessage('Password reset successfully');
      setPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        navigate('/sign-in');
      }, 2000);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (message) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-primary px-4 py-8">
        <div className="w-full max-w-sm text-center">
          <div className="mb-8 flex justify-center">
            <UntitledLogo className="h-8" />
          </div>
          <div className="rounded-lg bg-success-primary/10 p-4 text-sm text-success-primary">
            {message}
          </div>
          <p className="mt-4 text-sm text-tertiary">
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-primary px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <UntitledLogo className="h-8" />
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
            Set new password
          </h1>
          <p className="mt-2 text-sm text-tertiary">
            Please enter your new password below.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-error-primary/10 p-4 text-sm text-error-primary">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-primary"
            >
              New password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(value: string) => {
                  setPassword(value);
                  setError(null);
                }}
                isInvalid={!!error}
                isDisabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-quaternary hover:text-fg-quaternary/80"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="size-5" />
                ) : (
                  <Eye className="size-5" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-primary"
            >
              Confirm password
            </label>
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(value: string) => {
                setConfirmPassword(value);
                setError(null);
              }}
              isInvalid={!!error}
              isDisabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" isDisabled={isLoading}>
            {isLoading ? 'Resetting...' : 'Reset password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
